import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Boxes, Package, ArrowDownToLine, ArrowUpFromLine, Truck } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  findWarehouse,
  stockRowsOf, stockTransfersOf,
  findCatalogProduct, variantLabelOf, catalogProductsOf,
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';

const transferDirection = (t, warehouseId) => {
  if (t.fromLocationId === warehouseId) return 'out';
  if (t.toLocationId === warehouseId) return 'in';
  return null;
};

export const WhStockTab = ({ warehouseId, org }) => {
  const { version: dataVersion } = useSharedStore();
  const warehouse = useMemo(() => findWarehouse(warehouseId), [warehouseId, dataVersion]);
  const products = useMemo(() => catalogProductsOf(org.id), [org.id, dataVersion]);

  // Group received POs by product+variant → incoming qty.
  // Subtract outgoing transfer lines by matching name + label (transfers don't
  // store productId, so name matching is the best the seed shape gives us).
  const rows = useMemo(() => {
    const byKey = new Map();
    const ensure = (productId, variantId, srProduct) => {
      const key = `${productId}:${variantId || ''}`;
      let row = byKey.get(key);
      if (!row) {
        const product = findCatalogProduct(productId) || srProduct;
        let vLabel = variantLabelOf(product, variantId);
        if (!vLabel && srProduct?.variants) {
           const v = srProduct.variants.find(v => v.id === variantId);
           if (v) vLabel = v.label;
        }
        row = {
          key,
          productId,
          variantId: variantId || '',
          productName: product?.name || productId,
          variantLabel: vLabel,
          received: 0,
          dispatched: 0,
        };
        byKey.set(key, row);
      }
      return row;
    };

    stockRowsOf(org.id, warehouseId, 'active')
      .forEach((sr) => {
        const row = ensure(sr.productId, sr.variantId, sr.product);
        row.received += Number(sr.qty) || 0;
      });

    const transfers = stockTransfersOf(org.id).filter((t) => transferDirection(t, warehouseId) === 'out' && t.status !== 'cancelled');
    transfers.forEach((t) => {
      (t.lines || []).forEach((line) => {
        const product = products.find((p) => p.name === line.name);
        if (!product) return;
        const variant = (product.variants || []).find((v) => v.label === line.label);
        const row = ensure(product.id, variant?.id || '');
        row.dispatched += Number(line.qty) || 0;
      });
    });

    return Array.from(byKey.values()).map((r) => ({ ...r, onHand: Math.max(0, r.received - r.dispatched) }));
  }, [warehouseId, org.id, products, dataVersion]);

  if (!warehouse) {
    return (
      <div className="py-16 text-center text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
        Warehouse not found.
      </div>
    );
  }

  const accent = org.theme.primary;
  const totalReceived = rows.reduce((s, r) => s + r.received, 0);
  const totalDispatched = rows.reduce((s, r) => s + r.dispatched, 0);
  const totalOnHand = rows.reduce((s, r) => s + r.onHand, 0);

  return (
    <div className="flex flex-col gap-5">
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="Received total" value={totalReceived} icon={ArrowDownToLine} color="#0a7d3c" />
        <StatCard label="Dispatched out" value={totalDispatched} icon={ArrowUpFromLine} color="#b45309" />
        <StatCard label="On hand" value={totalOnHand} icon={Boxes} color={accent} />
      </section>

      <motion.section
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-[var(--theme-tertiary)] rounded-lg p-6 shadow-sm flex flex-col gap-3"
      >
        <header>
          <h3 className="text-base font-extrabold tracking-tight text-[#0d0d0d] inline-flex items-center gap-2">
            <Package size={16} style={{ color: accent }} aria-hidden="true" />
            Warehouse stock · {warehouse.name}
          </h3>
          <p className="text-[11px] font-medium text-slate-500 mt-1">
            On-hand = received via purchase orders − dispatched to branches via transports.
          </p>
        </header>

        {rows.length === 0 ? (
          <div className="py-12 border border-dashed border-[var(--theme-tertiary)] rounded-lg text-center">
            <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">No stock yet.</p>
            <p className="text-[11px] font-medium text-slate-500 mt-1">Add purchase orders in the WH Purchases tab to bring stock into this warehouse.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 border-b border-[var(--theme-tertiary)]">
                  <th className="py-2 pr-3">Product</th>
                  <th className="py-2 pr-3">Variant</th>
                  <th className="py-2 pr-3 text-right">Received</th>
                  <th className="py-2 pr-3 text-right">Dispatched</th>
                  <th className="py-2 pr-3 text-right">On hand</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-b border-[var(--theme-tertiary)] last:border-b-0">
                    <td className="py-2 pr-3 font-bold text-[#0d0d0d]">{r.productName}</td>
                    <td className="py-2 pr-3 text-slate-600">{r.variantLabel}</td>
                    <td className="py-2 pr-3 text-emerald-700 text-right">{r.received}</td>
                    <td className="py-2 pr-3 text-amber-700 text-right">{r.dispatched}</td>
                    <td className="py-2 pr-3 text-right font-extrabold" style={{ color: r.onHand > 0 ? accent : '#94a3b8' }}>{r.onHand}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.section>
    </div>
  );
};

WhStockTab.propTypes = {
  warehouseId: PropTypes.string.isRequired,
  org: PropTypes.object.isRequired,
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-4 py-3 flex items-center gap-3">
    <span className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: color }} aria-hidden="true">
      <Icon size={16} />
    </span>
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</p>
      <p className="text-base font-extrabold text-[#0d0d0d] leading-tight">{value}</p>
    </div>
    <Truck size={14} className="ml-auto text-slate-300 hidden sm:inline" aria-hidden="true" />
  </div>
);

StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.string.isRequired,
};
